import { Feature, Map as OlMap } from "ol";
import { Extent } from "ol/extent";
import { FeatureLike } from "ol/Feature";
import { Layer, Vector as VectorLayer, Tile as TileLayer, Group as LayerGroup } from "ol/layer";
import TileGrid from "ol/tilegrid/TileGrid";
import { Projection } from "ol/proj";
import { OSM, TileImage, Vector as VectorSource, Cluster as ClusterSource, ImageWMS } from "ol/source";
import { Fill, Icon, Stroke, Style, Text, Circle as CircleStyle } from "ol/style";
import { StyleLike } from "ol/style/Style";
import ClusterLayer from "ol-ext/layer/AnimatedCluster";
import Chart from "ol-ext/style/Chart";

import icons, { flagTypeMarkerNames, presetMarkerNames } from "../../img/icon/icons";
import { Color, isDev } from "../../components/common/Common";
import proj4 from "proj4";
import { register } from "ol/proj/proj4";
import ImageLayer from "ol/layer/Image";
import { bbox } from "@turf/turf";
import { BASE_URL, DEV_BASE_URL } from "../../data_provider/Config";
import BaseLayer from "ol/layer/Base";

proj4.defs("EPSG:5179", "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");
register(proj4);

// interfaces, enums, types -------------------------------
interface MarkerSizeOption {
    markerScale: number;
    tagOffsetX?: number;
    tagOffsetY?: number;
}

/**
 * 마커 스타일이 여러 개일 때 배열에 들어가는 순서
 */
enum MarkerStyleOrder {
    basicMarker,
    tag,
}

/**
 * 마커를 그릴 때 사용하는 옵션 인터페이스
 * @interface
 * @prop {string|{@link HTMLImageElement}} url 커스텀 마커의 아이콘 이미지|인기 마커 이미지 요소
 * @prop {string} color 마커 색상
 * @prop {"famous" | "custom" | "preset" | "flag"} type 마커 타입
 */
interface MarkerDrawOption {
    url: string | HTMLImageElement;
    color: string;
    type: "famous" | "custom" | "preset" | "flag";
}

interface MarkerStyleCache {
    [category: string]: Style;
}

interface MarkerOptions {
    label: string;
    color: string;
    zIndex?: number;
}

enum LayerName {
    BASE = "base", // 배경지도
    FEATURE = "feature", // 피처 레이어 -> 일반 레이어라서 visible false 상태로 사용
    REGION = "region",
    CLUSTER = "cluster",
    CLUSTER_OVERLAY = "Cluster overlay", // SelectCluster interaction에서 자동 생성하는 레이어, 변경 금지
    ADD = "add", // 장소 추가용
    

    CHOROPLETH = "choropleth", // 단계구분도
    CHART = "chart", // 차트 분석
    BUBBLE = "bubble",
    HEAT = "heat",
    SLOPE = "slope", // 경사도 분석

    DIRECTION = "direction", // 시간별 이동거리
    ISOCHORNE = "isochrone", // D's
    ROUTE = "route", // D's
    STARTPOINT = "startPoint", // D's

    ExtentAnalysis = "ExtentAnalysis", // 범위분석
    OVERLAP = "overlap", // 지도 오버랩
    ACCESSIBILITY = "accessibility",

    CALCULATE = "calculate", // 계산도구

    EDIT = "edit", // 장소 수정용
}

// styles ---------------------------------------
const addPointStyle = new Style({
    image: new Icon({
        src: (icons as any)["marker"],
        color: Color.ACCENT,
        scale: 0.8,
    }),
    text: new Text({
        font: "12px NotoSansB",
        text: "드래그하여 위치 이동",
        textAlign: "center",
        offsetY: 35,
        backgroundFill: new Fill({
            color: "#fff",
        }),
        backgroundStroke: new Stroke({
            color: "#fff",
            width: 6,
            lineCap: "round",
            lineJoin: "round",
        }),
        padding: [0, 0, 0, 2],
    }),
});

const markerTag = {
    new: new Style({
        text: new Text({
            font: "12px NotoSansEB",
            text: "NEW",
            // offsetX: markerOptions.DEFAULT.tagOffsetX,
            // offsetY: markerOptions.DEFAULT.tagOffsetY,
            fill: new Fill({
                color: "#fff",
            }),
            backgroundFill: new Fill({
                color: Color.MAIN,
            }),
            padding: [1, 1, 1, 2],
        }),
    }),
};

const markerSizeOptions: { [key: string]: MarkerSizeOption } = {
    DEFAULT: {
        markerScale: 0.18,
        tagOffsetX: 18,
        tagOffsetY: -36,
    },
    LARGE: {
        markerScale: 0.27,
        tagOffsetX: 22,
        tagOffsetY: -46,
    },
    FAMOUS_DEFAULT: {
        markerScale: 0.3,
        tagOffsetX: 16,
        tagOffsetY: -55,
    },
    FAMOUS_LARGE: {
        markerScale: 0.4,
        tagOffsetX: 20,
        tagOffsetY: -60,
    },
};

const markerDrawOptions = {
    ["famous"]: {
        canvasSize: 200,
        iconSize: 120,
        verticalPad: 10,
        markerRadius: 70,
        tailLength: 35,
    },
    ["preset"]: {
        canvasSize: 150,
        iconSize: 95,
        verticalPad: 10,
        markerRadius: 70,
        tailLength: 35,
    },
    ["custom"]: {
        canvasSize: 200,
        iconSize: 95,
        verticalPad: 10,
        markerRadius: 70,
        tailLength: 35,
    },
    ["flag"]: {
        canvasSize: 150,
        iconSize: 95,
        verticalPad: 10,
        markerRadius: 70,
        tailLength: 35,
    },
};

const markerStyleCache: { [category: string]: Style } = {};

// marker --------------------------------------------
const getMarkerStyle = (option: MarkerOptions) => {
    return new Style({
        text: new Text({
            font: "12px NotoSansB",
            offsetY: 10,
            stroke: new Stroke({
                color: "#fff",
                width: 3,
            }),
            fill: new Fill({
                color: option.color,
            }),
            // text: option.label.length < 7 ? option.label : splitLabel(option.label),
            text: option.label,
        }),
        zIndex: option.zIndex,
    });
};

const getMarkerIconStyle = (url: string, scale: number) => {
    return new Icon({
        anchor: [0.5, 1],
        anchorXUnits: "fraction",
        anchorYUnits: "fraction",
        src: url,
        scale: scale,
    });
};

/**
 * 라벨 문자열 줄바꿈 함수
 * @param label 라벨 문자열
 * @returns 줄바꿈 처리된 라벨 문자열 {{@link String}}
 */
const splitLabel = (label: string) => {
    const firstLine = label.substring(0, 6);
    const secondLine = label.length > 10 ? `${label.substring(6, 4)}...` : label.substring(6);

    return `${firstLine}\n${secondLine}`;
};

const markerStyleFunction = (featureLike: FeatureLike) => {
    const styles: Style[] = [];
    const feature = featureLike as Feature;
    const { famousImg, category, color, label, icon, isNew } = feature.getProperties();
    
    Object.values(MarkerStyleOrder).forEach((idx) => {
        switch (idx) {
            case MarkerStyleOrder.basicMarker: {
                let style = markerStyleCache[category];
                if (style) {
                    const _style = style.clone();
                    _style.getText().setText(label);
                    styles.splice(idx, 0, _style);
                    break;
                }

                style = getMarkerStyle({
                    color: color,
                    label: label,
                    zIndex: 2,
                });
                styles.splice(idx, 0, style);

                if (famousImg) {
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.src = famousImg;

                    img.onload = () => {
                        setMarkerIcon(
                            {
                                url: img,
                                type: "famous",
                                color: color,
                            },
                            feature,
                            MarkerStyleOrder.basicMarker
                        );
                    };
                    break;
                }

                // console.log("[DEBUG] olmap icons", icons);
                // console.log("[DEBUG] olmap icon", icon);

                fetch(icons[icon])
                    .then((res) => (res.ok ? res.text() : undefined))
                    .then((markUp) => {
                        const isPreset = presetMarkerNames.includes(icon);
                        const isFlagType = flagTypeMarkerNames.includes(icon); // 깃발 형태의 마커일 경우 깃대가 중심에 맞도록 이동

                        markUp &&
                            setMarkerIcon(
                                {
                                    url: markUp,
                                    type: isPreset ? (isFlagType ? "flag" : "preset") : "custom",
                                    color: color,
                                },
                                feature,
                                MarkerStyleOrder.basicMarker
                            );
                    });

                break;
            }
            case MarkerStyleOrder.tag: {
                let style: Style | undefined = undefined;

                if (isNew) {
                    style = markerTag.new.clone(); // TODO mjkang 장소 추가 미리보기 마커 위에 올라가는 태그 디자인 변경
                } else if (famousImg) {
                    style = new Style({
                        image: new Icon({
                            rotation: Math.PI / 5,
                            src: icons["crownSimple"],
                            displacement: [-20, 53],
                            scale: 0.025,
                        }),
                        zIndex: 1, // FIXME mjkang 줌인아웃 시 마커와 순서가 뒤바뀌는 문제 있음..
                    });
                }

                style && styles.splice(idx, 0, style);
                break;
            }
        }
    });

    feature.setStyle(styles);
    return styles;
};

/**
 * 커스텀 마커 및 인기 마커를 canvas로 그리는 함수
 *
 * @param option {@link MarkerDrawOption} 마커를 그리기 위해 필요한 옵션
 * @returns HTMLCanvasElement {{@link HTMLCanvasElement}}
 * */
const drawMarkerIcon = (option: MarkerDrawOption) => {
    const { canvasSize, iconSize, verticalPad, markerRadius, tailLength } = markerDrawOptions[option.type];
    const [centerX, centerY] = [canvasSize / 2, canvasSize / 2 - verticalPad * 2];

    const canvas = document.createElement("canvas");
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    const ctx = canvas.getContext("2d");

    if (!ctx) return canvas;
    ctx.globalCompositeOperation = "destination-over"; // 역순으로 그리기(처음 그린 요소가 가장 위로 올라오게)
    ctx.save();

    switch (option.type) {
        case "preset": {
            const dom = new DOMParser().parseFromString(option.url as string, "image/svg+xml");
            const [, , w, h] = dom.querySelector("svg")?.getAttribute("viewBox")?.split(" ") ?? [0, 0, iconSize, iconSize];

            let pathData = dom.querySelector("[d]")?.getAttribute("d");
            if (!pathData) return canvas;

            let path = new Path2D(pathData);

            // svg로 아이콘 그리기
            const ratio = canvasSize / Math.max(Number(h), Number(w)); // 아이콘 실제 크기와 캔버스 사이즈 간의 비율
            const resizedW = Number(w) * ratio;

            canvas.width = canvasSize;

            ctx.translate((canvasSize - resizedW) / 2, canvasSize);
            ctx.scale(ratio, -ratio);

            ctx.fillStyle = option.color;
            ctx.fill(path);

            return canvas;
        }
        case "flag": {
            const dom = new DOMParser().parseFromString(option.url as string, "image/svg+xml");
            const [, , w, h] = dom.querySelector("svg")?.getAttribute("viewBox")?.split(" ") ?? [0, 0, iconSize, iconSize];

            let pathData = dom.querySelector("[d]")?.getAttribute("d");
            if (!pathData) return canvas;

            let path = new Path2D(pathData);

            // svg로 아이콘 그리기
            const ratio = canvasSize / Math.max(Number(h), Number(w)); // 아이콘 실제 크기와 캔버스 사이즈 간의 비율
            const resizedW = Number(w) * ratio;
            const leftPadding = resizedW / 2;

            canvas.width = canvasSize + leftPadding;

            ctx.translate((canvasSize - resizedW) / 2 + leftPadding, canvasSize);
            ctx.scale(ratio, -ratio);

            ctx.fillStyle = option.color;
            ctx.fill(path);

            return canvas;
        }
        case "famous": {
            // 1. 인기 마커에 들어갈 사진 원 마스킹하여 그리기
            ctx.arc(centerX, centerY, markerRadius - 10, Math.PI, -Math.PI);
            ctx.clip();
            ctx.drawImage(option.url as HTMLImageElement, centerX - iconSize / 2, centerY - iconSize / 2, iconSize, iconSize);
            break;
        }
        case "custom": {
            const dom = new DOMParser().parseFromString(option.url as string, "image/svg+xml");
            const [x, y, w, h] = dom.querySelector("svg")?.getAttribute("viewBox")?.split(" ") ?? [0, 0, iconSize, iconSize];

            const pathData = dom.querySelector("[d]")?.getAttribute("d");
            if (!pathData) return canvas;

            const path = new Path2D(pathData);

            // 1. svg로 아이콘 그리기
            // svg 파일의 viewBox 설정에 따라 svg의 원점(origin)과 크기가 다르므로 설정에 맞추어 path를 그려야 함
            ctx.scale(iconSize / Number(w), iconSize / Number(h));
            ctx.translate(-x + (Number(w) / iconSize) * (centerX - iconSize / 2), -y + (Number(h) / iconSize) * (centerY - iconSize / 2));
            ctx.fillStyle = option.color;
            ctx.fill(path);
            break;
        }
    }

    // 마스킹 리셋
    ctx.restore();

    // 2. 배경 원 그리기
    ctx.arc(centerX, centerY, markerRadius - (option.type === "famous" ? 15 : 5), Math.PI, -Math.PI);
    ctx.fillStyle = "white";
    ctx.fill();

    // 3. 마커 아이콘 그리기
    ctx.beginPath();
    ctx.arc(centerX, centerY, markerRadius, (Math.PI * 3) / 4, Math.PI / 4);
    ctx.moveTo(centerX - markerRadius / Math.sqrt(2), centerY + markerRadius / Math.sqrt(2));
    ctx.lineTo(centerX, markerRadius * 2 + verticalPad + tailLength);
    ctx.lineTo(centerX + markerRadius / Math.sqrt(2), centerY + markerRadius / Math.sqrt(2));
    ctx.closePath();
    ctx.fillStyle = option.color;
    ctx.fill();

    return canvas;
};

/**
 * canvas로 그린 마커 이미지가 로드된 후 피처 스타일에 적용하는 함수
 *
 * @param option {@link MarkerDrawOption} 마커를 그리기 위한 옵션
 * @param feature {@link Feature} 마커 디자인을 적용할 피처 객체
 * @param styleIdx {@link Number} (optional) 스타일이 여러 개인 경우(배열)
 */

const setMarkerIcon = (option: MarkerDrawOption, feature: Feature, styleIdx?: MarkerStyleOrder) => {
    const { type } = option;
    const { markerScale } = markerSizeOptions[type === "famous" ? "FAMOUS_DEFAULT" : "DEFAULT"];

    const canvas = drawMarkerIcon(option);

    const icon = getMarkerIconStyle(canvas.toDataURL(), markerScale);
    if (styleIdx === undefined) {
        const style = feature.getStyle() as Style;
         if (style) {
            style.setImage(icon);
            feature.setStyle(style);
        }
    } else {
        const styles = (feature.getStyle() as Style[]) ?? [];
        const style = styles[styleIdx];

        if (style) {
            style.setImage(icon);
            feature.setStyle(style);
        }
    }
};

const defaultClusterStyle = new Style({
    image: new CircleStyle({
        radius: 5,
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({
            color: "black",
            width: 1,
        }),
    }),
});

const sharedStyleCache: { [key: string]: Style } = {};

const getSharedStyle = (feature: Feature): Style => {
    const category = feature.get("category");
    const color = feature.get("color");
    const label = feature.get("label");
    const key = `${category}_${color}`;
    const markerStyle = feature.get("icon");

    // 캐시에 없으면 새로 만들기
    if (!sharedStyleCache[key]) {
        // 25.08.29 | Kim Young Jun | markerStyle 또는 icons 매핑 누락 시 기본 아이콘으로 폴백하여 Icon 생성 실패(AssertionError) 방지
        const iconSrc = (icons as any)[markerStyle] ?? (icons as any)["marker"];
        if (!iconSrc) {
            console.warn("getSharedStyle: 아이콘 소스가 없습니다. markerStyle=", markerStyle);
        }
        sharedStyleCache[key] = new Style({
            image: new Icon({
                src: iconSrc,
                color: color,
                scale: 0.5,
            }),
            text: new Text({
                text: label || "",
                font: "12px NotoSansB",
                fill: new Fill({ color: color }),
                stroke: new Stroke({ color: "white", width: 3 }),
                offsetY: 15,
                textAlign: "center",
                textBaseline: "top",
            }),
        });
    }

    // label은 피처마다 다를 수 있으니 clone해서 text만 수정
    const style = sharedStyleCache[key].clone();
    style.getText()?.setText(label ?? "");

    return style;
};

const TXT_FILL_WHITE = new Fill({ color: '#fff' });
const STROKE = new Stroke({ color: 'black', width: 1 })
const PIE_STYLE_CACHE = new Map<string, Style>();

function getPieClusterStyle(radius: number, colors: string[], values: number[]) {
  const key = `${radius}|${colors.join('|')}|${values.join(',')}`;
  let s = PIE_STYLE_CACHE.get(key);
  if (!s) {
    s = new Style({
      image: new (Chart as any)({
        type: 'pie',
        radius,
        colors,
        data: values,
        stroke: STROKE,
      }),
      text: new Text({
        font: '14px NotoSansB',
        fill: TXT_FILL_WHITE,
        stroke: STROKE,
      }),
    });
    PIE_STYLE_CACHE.set(key, s);
  }
  // count는 values 합으로 동일하므로 여기서 세팅하면 per-feature 변경도 최소화
  const count = values.reduce((a, b) => a + b, 0);
  s.getText()?.setText(String(count));
  return s;
}

const ORDER_TOP = 5;    // 파이에 반영할 상위 색상 개수

const clusterStyleFunction = (featureLike: FeatureLike) => {
  const cluster = featureLike as Feature;
  const features = (cluster.get('features') as Feature[]) ?? [];
  const count = features.length;
  const radius = getClusterRadius(count);

  if (count === 0) return defaultClusterStyle; // clone 불필요: 고정심벌이면 재사용 OK

  if (count === 1) {
    const f = features[0];
    return f.get('selected') ? selectedMarkerStyleFunction(f) : getSharedStyle(f);
  }

  // 색상 분포 집계 (작은 것들은 'others'로 묶어 키 폭발 방지)
  const m: Record<string, number> = {};
  for (let i = 0; i < features.length; i++) {
    const c = features[i].get('color');
    if (c) m[c] = (m[c] || 0) + 1;
  }
  const sorted = Object.entries(m).sort((a,b) => b[1] - a[1]);
  const top = sorted.slice(0, ORDER_TOP);

  const colors: string[] = top.map(([c]) => c);
  const values: number[] = top.map(([,v]) => v);
  const others = sorted.slice(ORDER_TOP).reduce((s, [,v]) => s + v, 0);
  if (others) { colors.push('rgba(160,160,160,0.85)'); values.push(others); }

  return getPieClusterStyle(radius, colors, values);
};


/**
 * 클러스터 미리보기의 마커와 클러스터 사이의 라인 스타일
 *
 * @param featureLike 라인 객체
 * @returns style {{@link StyleLike}}
 */
const revealedStyleFunction = (featureLike: FeatureLike) => {
    const feature = featureLike as Feature;
    const type = feature.getGeometry()?.getType();

    if (type === "LineString") {
        return new Style({
            stroke: new Stroke({
                color: "rgba(255, 255, 255, 0.6)",
                width: 90,
            }),
        });
    }
};

/**
 * 클러스터/클러스터 미리보기 선택 시 마커 스타일
 *
 * @param featureLike 클러스터/클러스터 미리보기
 * @returns style {{@link StyleLike}}
 */
const selectedClusterStyleFunction = (featureLike: FeatureLike) => {
    console.log("[DEBUG] selectedClusterStyleFunction", featureLike);
    
    if (!featureLike.get("selectcluserfeatures")) {
        // 클러스터 미리보기 (하나만 있을 때)
        const feature = featureLike.get("features")[0] as Feature;
        if (feature) return selectedMarkerStyleFunction(feature);
    } else {
        // 클러스터를 클릭해서 펼쳐졌을 때
        const features = featureLike.get("selectcluserfeatures") as Feature[];
        const points = features.filter((f) => f.getGeometry()?.getType() === "Point");

        points.forEach((point) => {
            const feature = point.get("features")?.[0] as Feature;
            if (!feature) return;

            // 사이드패널에서 Feature을 선택해서 클러스터가 펴질때 
            // 선택된 Feature는 하이라이팅 시킨다.
            const style = feature.get('selected') 
                ? selectedMarkerStyleFunction(feature)
                : getSharedStyle(feature);
            
            point.setStyle(undefined)
            point.setStyle(style);
        });

        return new Style();
    }
    return new Style();
};

const selectedMarkerStyleFunction = (feature: Feature) => {
    const color = feature.get("color");
    const label = feature.get("label");
    const markerStyle = feature.get("icon");

    // console.log("[DEBUG] markerStyle", markerStyle);

    const scale = 0.7;

    const highlightStyle = new Style({
        image: new Icon({
            src: (icons as any)[
                markerStyle === 'markerSelect1' ? 'mk1_highlight' : 
                markerStyle === 'markerSelect2' ? 'mk2_highlight' : 
                markerStyle === 'markerSelect3' ? 'mk3_highlight' : 
                markerStyle
            ],
            scale: 1.4,
        }),
        zIndex: 1,
    })

    const mainStyle = new Style({
        image: new Icon({
            src: (icons as any)[markerStyle],
            color: Color.MAIN,
            scale: 0.7,
        }),
        text: new Text({
            text: label || "",
            font: "12px NotoSansB",
            fill: new Fill({ color: Color.MAIN }),
            stroke: new Stroke({ color: "white", width: 4 }),
            offsetY: 15,
            textAlign: "center",
            textBaseline: "top",
        }),
        zIndex: 2,
    });

    return [highlightStyle, mainStyle];
};

function getClusterRadius(size: number) {
    let result = 0;

    if (size > 1 && size <= 2) {
        result = 10;
    } else if (size > 2 && size <= 3) {
        result = 11;
    } else if (size > 3 && size <= 5) {
        result = 12;
    } else if (size > 5 && size <= 8) {
        result = 13;
    } else if (size > 8 && size <= 10) {
        result = 14;
    } else if (size > 10 && size <= 11) {
        result = 15;
    } else if (size > 11 && size <= 15) {
        result = 18;
    } else if (size > 15 && size <= 20) {
        result = 20;
    } else if (size > 20 && size <= 50) {
        result = 22;
    } else if (size > 50 && size <= 65) {
        result = 24;
    } else if (size > 65 && size <= 80) {
        result = 26;
    } else if (size > 80 && size <= 95) {
        result = 28;
    } else if (size > 95 && size <= 110) {
        result = 30;
    } else if (size > 110 && size <= 555) {
        result = 32;
    } else if (size > 555 && size <= 3000) {
        result = 34;
    } else {
        result = 36;
    }

    return result;
}

// layer ----------------------------------------------
const createVectorLayer = (name: LayerName | string) => {
    return new VectorLayer({
        source: new VectorSource({}),
        visible: false,
        properties: {
            name: name,
        },
    });
};

const createClusterLayer = (name: LayerName | string) => {
    const src = new ClusterSource({
      distance: 40,
      minDistance: 12,
      source: new VectorSource({
        wrapX: false,
      }),
    });
  
    const layer = new ClusterLayer({
      source: src as any,
      animationDuration: 400,
      properties: { name },
      zIndex: 2,
    });
  
    return layer;
  };

const createLayerGroup = (name: LayerName) => {
    return new LayerGroup({
        visible: false,
        properties: {
            name: name,
        },
        layers: [],
    });
};

const createBaseLayer = (mapType: string) => {
    switch (mapType.toUpperCase()) {
        case "BAROEMAP": // NGII
            return createNGIIMap();
        case "UMAP":
        default: // OSM
            return new TileLayer({
                source: new OSM(),
                properties: {
                    name: LayerName.BASE,
                },
            });
    }
};

const createNGIIMap = () => {
    const wmts_url = "https://map.ngii.go.kr/openapi/Gettile.do?";
    const wmts_options = "service=WMTS&request=GetTile&version=1.0.0&layer=base_hd&style=korean&format=image/png&tilematrixset=korean";

    const matrixIds = ["L05", "L06", "L07", "L08", "L09", "L10", "L11", "L12", "L13", "L14", "L15", "L16", "L17", "L18"];

    const maxResolution = 2088.96;
    let resolutions = [];

    for (let i = 0; i < 14; i++) {
        resolutions.push(maxResolution / Math.pow(2, i));
    }

    const NGIImap = new TileLayer({
        source: new TileImage({
            crossOrigin: "anonymous",
            tileGrid: new TileGrid({
                extent: [-200000.0, -3015.4524155292, 3803015.45241553, 4000000.0],
                origin: [-200000.0, 4000000.0],
                tileSize: [256, 256],
                resolutions: resolutions,
            }),
            tileUrlFunction: function ([level, col, row]) {
                return wmts_url + wmts_options + "&tilematrix=" + matrixIds[level] + "&tilecol=" + col + "&tilerow=" + row + "&apikey=1CAC05CD9FB7CA048DC4ED05A120599B";
            },
        }),
        visible: true,
        properties: {
            name: LayerName.BASE,
        },
    });

    NGIImap.setPreload(4);

    return NGIImap;
};

const createSlopeLayer = () => {

    // WMS 서버에서 경사도 레이어를 불러오기 위한 ImageLayer 생성

    //FIXME 모두의지도 통해서 갈 수 있게 수정
    const slopeLayer = new ImageLayer({
        source: new ImageWMS({
            url: 'https://modoomap.com/kMap_MariaDB/MapService?',
            params: {
            LAYERS: "vkorea_hexagon_slope_point_00,vkorea_hexagon_slope_point_01,vkorea_hexagon_slope_point_02,vkorea_hexagon_slope_point_03,vkorea_hexagon_slope_point_04,vkorea_hexagon_slope_point_05,vkorea_hexagon_slope_point_06,vkorea_hexagon_slope_point_07,vkorea_hexagon_slope_point_08,vkorea_hexagon_slope_point_09,vkorea_hexagon_slope_point_10,vkorea_hexagon_slope_point_11,vkorea_hexagon_slope_00,vkorea_hexagon_slope_01,vkorea_hexagon_slope_02,vkorea_hexagon_slope_03,vkorea_hexagon_slope_04,vkorea_hexagon_slope_05",
            STYLES: "",
            FORMAT: "image/png",
            TRANSPARENT: true,
            CRS: "EPSG:5179",
            },
            crossOrigin: "anonymous",
        }),
    });
    slopeLayer.set("name", LayerName.SLOPE);
    return slopeLayer;
}

const getLayer = (parent: OlMap | LayerGroup, name: string) => {
    parent = parent instanceof OlMap ? parent.getLayerGroup() : parent;
    const layer = parent.getLayersArray().find((layer) => layer.get("name") === name);

    return layer ? (layer as Layer) : undefined;
};

const getLayerGroup = (map: OlMap, name: string) => {
    const group = map
        .getLayers()
        .getArray()
        .find((layer) => layer.get("name") === name);

    if (group instanceof LayerGroup) return group as LayerGroup;

    return undefined;
};

const getInteraction = (map: OlMap, name: LayerName) => {
    return map
        .getInteractions()
        .getArray()
        .find((i) => i.get("name") === name);
};

const getInteractions = (map: OlMap, name: LayerName) => {
    return map
        .getInteractions()
        .getArray()
        .filter((i) => i.get("name") === name);
};

const removeInteractions = (map: OlMap, name: LayerName) => {
    const interactions = getInteractions(map, name);
    interactions.forEach((i) => map.removeInteraction(i));
};

const getOverlays = (map: OlMap, name: LayerName) => {
    return map
        .getOverlays()
        .getArray()
        .filter((o) => o.get("name") === name);
};

const removeOverlays = (map: OlMap, name: LayerName) => {
    const overlays = getOverlays(map, name);
    overlays.forEach((o) => map.removeOverlay(o));
};

// modules ---------------------------------------------
// const fitToExtent = (map: OlMap, extent: Extent) => {
//     map.getView().fit(extent, {
//         padding: [100, 100, 100, 100],
//     });
// };

/**
 * 지도 영역을 애니메이션과 함께 맞추는 함수
 * 25.07.08 | Kim Young Jun | fit 기능 애니메이션 추가
 * @param map OlMap 객체
 * @param extent 맞출 영역
 * @param duration 애니메이션 지속 시간 (밀리초, 기본값: 500)
 * @param padding 영역 주변 여백 (기본값: [100, 100, 100, 100])
 */
const fitToExtent = (map: OlMap, extent: Extent, duration: number = 500, padding?: number[]) => {
    if (!map || !extent || extent.some(isNaN)) return;
    
    // 영역이 점이나 선인 경우 최소 영역 확보
    if (extent[0] === extent[2] || extent[1] === extent[3]) {
        const view = map.getView();
        const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
        const resolution = view.getResolution() || 1;
        const newExtent = [
            center[0] - resolution * 50,
            center[1] - resolution * 50,
            center[0] + resolution * 50,
            center[1] + resolution * 50
        ];
        extent = newExtent;
    }
    
    map.getView().fit(extent, {
        padding: padding || [100, 100, 100, 100],
        duration: duration,  // 애니메이션 지속 시간
        easing: (t: number) => {
            // easeOutQuad 애니메이션 이징 효과
            return t * (2 - t);
        }
    });
};

const getProjection = (mapType: string) => {
    // !@ gsshin (2024.06.17) : EPSG:5179 정의가 최상단으로 이동
    // proj4.defs("EPSG:5179", "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");
    // register(proj4);

    switch (mapType.toUpperCase()) {
        case "BAROEMAP": // NGII
            return new Projection({
                code: "EPSG:5179",
                extent: [531371.84, 957856.66, 1663741.9, 2274021.31],
                units: "m",
            });
        case "UMAP":
        default: // OSM
            return new Projection({
                code: "EPSG:3857",
                extent: [-20037508.34, -20048966.1, 20037508.34, 20048966.1],
                units: "m",
            });
    }
};

/**
 * Vector / Cluster 소스에 쌓인 feature들을 명시적으로 제거
 */
const clearLayerSource = (layer: BaseLayer) => {
	const anyLayer = layer as any;
	const src = anyLayer?.getSource?.();
	if (!src) return;

	try {
		// ClusterSource -> 내부 VectorSource 비우기
		if (typeof src.getSource === "function") {
			const inner = src.getSource?.();
			if (inner && typeof inner.clear === "function") {
				inner.clear(true);
			}
			return;
		}

		// VectorSource 비우기
		if (typeof src.clear === "function") {
			src.clear(true);
		}
	} catch {
		// ignore
	}

	// 가능한 경우 source 참조 끊기 (GC 도움)
	try {
		anyLayer?.setSource?.(null);
	} catch {
		// ignore
	}
};

/**
 * gsshin (2026.01.23) : Overlay 제거 시, overlay element가 DOM에 남는 케이스가 있어 element.remove()까지 수행
 */
const removeOverlayElements = (map: OlMap) => {
	try {
		const overlays = map.getOverlays().getArray();
		overlays.forEach((o: any) => {
			const el = o?.getElement?.();
			if (el && typeof el.remove === "function") {
				el.remove();
			}
		});
	} catch {
		// ignore
	}
};

/**
 * gsshin (2026.01.23) : 화면 dispose 시 호출 하여 OpenLayers Map이 잡고 있는 대부분의 참조를 끊어 GC 되도록 유도
 */
export const disposeOlMap = (map: OlMap | null | undefined) => {
	if (!map) return;

	try {
		// 1) overlay element DOM 제거 + overlay 제거
		removeOverlayElements(map);
		map.getOverlays().clear();

		// 2) interaction 제거
		map.getInteractions().clear();

		// 3) control 제거 (사용 중이면 메모리/이벤트 남는 경우 있음)
		map.getControls().clear();

		// 4) 레이어 source 정리 후 레이어 제거
		map.getLayers()
			.getArray()
			.forEach((layer) => {
				clearLayerSource(layer as any);
			});
		map.getLayers().clear();
		
		// 5) target detach
		map.setTarget(undefined);
	} catch (e){
		console.error("disposeOlMap error:", e);
	
	} finally {
		// 어떤 상황에서도 target detach는 보장
		try {
			map.setTarget(undefined);
		} catch {
			// ignore
		}

        // 전역 참조 정리
        if (typeof window !== "undefined") {
            const w = window as any;
            if (w.olMap === map) w.olMap = null;
        }
	}
};



export {
    createBaseLayer,
    createVectorLayer,
    createClusterLayer,
    createLayerGroup,
    createSlopeLayer,
    markerStyleFunction,
    clusterStyleFunction,
    revealedStyleFunction,
    selectedClusterStyleFunction,
    getLayer,
    getLayerGroup,
    getInteraction,
    getInteractions,
    removeInteractions,
    getOverlays,
    removeOverlays,
    getProjection,
    fitToExtent,
    addPointStyle,
    LayerName,
    type MarkerStyleCache,
    getSharedStyle,
};
